import React from "react";
import useTranslation from "hooks/useTranslation";
import "./style.scss";

const AppUI = () => (
	<svg
		width="100%"
		height="100%"
		viewBox="0 0 380 220"
		preserveAspectRatio="xMidYMid slice"
	>
		{/* header */}
		<rect width="380" height="28" fill="#0d1b2e" />
		<rect x="8" y="6" width="48" height="16" rx="3" fill="#2078FF" />
		<text
			x="32"
			y="18"
			textAnchor="middle"
			fontSize="7"
			fontWeight="900"
			fill="white"
			fontStyle="italic"
		>
			ZIM·ZIM
		</text>
		<rect x="64" y="8" width="55" height="12" rx="2" fill="#1e3a5f" />
		<circle cx="330" cy="14" r="7" fill="#1e3a5f" />
		<circle cx="348" cy="14" r="7" fill="#1e3a5f" />
		<rect x="360" y="6" width="14" height="16" rx="2" fill="#CA8A04" opacity="0.7" />

		{/* sidebar */}
		<rect y="28" width="62" height="192" fill="#0d1b2e" />
		{[0, 1, 2, 3, 4, 5, 6].map(i => (
			<g key={i}>
				<rect
					x="6"
					y={36 + i * 24}
					width="50"
					height="16"
					rx="3"
					fill={i === 0 ? "#2A85FF" : "#162d4e"}
					opacity={i === 0 ? 1 : 0.7}
				/>
				<rect x="12" y={41 + i * 24} width="22" height="4" rx="2" fill="white" opacity={i === 0 ? 0.9 : 0.5} />
				<rect x="12" y={47 + i * 24} width="14" height="3" rx="1.5" fill="white" opacity="0.2" />
			</g>
		))}

		{/* content */}
		<rect x="62" y="28" width="318" height="192" fill="#f4f6f9" />
		{/* toolbar */}
		<rect x="62" y="28" width="318" height="32" fill="white" />
		<rect x="70" y="36" width="80" height="16" rx="8" fill="#e8f0fe" />
		<rect x="74" y="40" width="72" height="8" rx="4" fill="#2A85FF" opacity="0.35" />
		<rect x="300" y="34" width="70" height="18" rx="4" fill="#2A85FF" />
		<rect x="305" y="39" width="60" height="8" rx="4" fill="white" opacity="0.8" />

		{/* table head */}
		<rect x="62" y="66" width="318" height="18" fill="#eef2f7" />
		{[0, 1, 2, 3, 4].map(i => (
			<rect key={i} x={72 + i * 57} y="71" width="44" height="7" rx="3" fill="#94a3b8" opacity="0.8" />
		))}

		{/* rows */}
		{[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
			<g key={i}>
				<rect x="62" y={85 + i * 17} width="318" height="16" fill={i % 2 === 0 ? "white" : "#f8fafc"} />
				<rect x="70" y={90 + i * 17} width="36" height="6" rx="3" fill="#0d1b2e" opacity="0.6" />
				<rect x="116" y={90 + i * 17} width="32" height="6" rx="3" fill={i % 3 === 0 ? "#16a34a" : "#EAB308"} opacity="0.75" />
				<rect x="174" y={90 + i * 17} width="26" height="6" rx="3" fill="#94a3b8" opacity="0.55" />
				<rect x="230" y={90 + i * 17} width="38" height="6" rx="3" fill="#94a3b8" opacity="0.45" />
				<rect x="300" y={90 + i * 17} width="30" height="6" rx="3" fill="#2A85FF" opacity="0.4" />
			</g>
		))}
	</svg>
);

const Gear = ({ size, color, style }) => (
	<svg width={size} height={size} viewBox="0 0 84 84" fill="none" style={style}>
		<path
			d="M42 24a2 2 0 00-2 2v3.2a14 14 0 00-5 2.1l-2.3-2.3a2 2 0 00-2.8 0l-2.8 2.8a2 2 0 000 2.8l2.3 2.3A14 14 0 0028 40h-3.2a2 2 0 00-2 2v4a2 2 0 002 2H28a14 14 0 001.9 5.2l-2.3 2.3a2 2 0 000 2.8l2.8 2.8a2 2 0 002.8 0l2.3-2.3A14 14 0 0040 60.4v3.2a2 2 0 002 2h4a2 2 0 002-2v-3.2a14 14 0 005.2-1.9l2.3 2.3a2 2 0 002.8 0l2.8-2.8a2 2 0 000-2.8l-2.3-2.3A14 14 0 0060 48h3.2a2 2 0 002-2v-4a2 2 0 00-2-2H60a14 14 0 00-1.9-5.2l2.3-2.3a2 2 0 000-2.8l-2.8-2.8a2 2 0 00-2.8 0l-2.3 2.3A14 14 0 0048 27.2V24a2 2 0 00-2-2h-4z"
			fill={color}
		/>
		<circle cx="44" cy="44" r="9" fill="white" opacity="0.18" />
		<circle cx="44" cy="44" r="5" fill="white" opacity="0.12" />
	</svg>
);

/** Vertical slice of traffic cone (apex → base), reference-accurate stripe layout */
function maintConeTrapezoid(yTop, yBot, cx = 50, apexY = 10, baseY = 76, baseHalf = 30) {
	const span = baseY - apexY;
	const left = y => cx - (baseHalf * (y - apexY)) / span;
	const right = y => cx + (baseHalf * (y - apexY)) / span;
	const l0 = left(yTop);
	const r0 = right(yTop);
	const l1 = left(yBot);
	const r1 = right(yBot);
	return `M ${l0} ${yTop} L ${r0} ${yTop} L ${r1} ${yBot} L ${l1} ${yBot} Z`;
}

const Maintenance = () => {
	const { tr } = useTranslation();

	const coneTip = maintConeTrapezoid(10, 22);
	const coneWhite1 = maintConeTrapezoid(22, 36);
	const coneOrangeMid = maintConeTrapezoid(36, 50);
	const coneWhite2 = maintConeTrapezoid(50, 66);
	const coneOrangeBot = maintConeTrapezoid(66, 76);

	return (
		<div className="maintenance">
			<div className="maintenance__blob-red" />
			<div className="maintenance__blob-blue" />

			<div className="maintenance__gear" style={{ top: "6%", left: "3%", animation: "gCW 10s linear infinite" }}>
				<Gear size={90} color="#9CA3AF" />
			</div>
			<div className="maintenance__gear" style={{ top: "12%", left: "9%", animation: "gCCW 7s linear infinite" }}>
				<Gear size={54} color="#6B7280" />
			</div>
			<div className="maintenance__gear" style={{ bottom: "8%", right: "4%", animation: "gCW 8s linear infinite" }}>
				<Gear size={80} color="#94A3B8" />
			</div>
			<div className="maintenance__gear" style={{ bottom: "18%", right: "11%", animation: "gCCW 12s linear infinite" }}>
				<Gear size={46} color="#64748B" />
			</div>
			<div className="maintenance__gear" style={{ top: "50%", left: "1%", animation: "gCW 14s linear infinite" }}>
				<Gear size={36} color="#4B5563" />
			</div>

			<div className="maintenance__card">
				<div className="maintenance__left">
					<div className="maintenance__laptop-wrap">
						<div className="maintenance__screen">
							<div className="maintenance__cam" />
							<div className="maintenance__screen-inner">
								<AppUI />
							</div>
							<div className="maintenance__glare" />
						</div>
						<div className="maintenance__hinge" />
						<div className="maintenance__base">
							<div className="maintenance__pad" />
						</div>
					</div>

					<svg viewBox="0 0 100 108" className="maintenance__cone">
						<defs>
							<linearGradient id="maintConeOrange" x1="0" y1="0" x2="1" y2="0">
								<stop offset="0%" stopColor="#ffa64d" />
								<stop offset="38%" stopColor="#ff7f24" />
								<stop offset="100%" stopColor="#c63612" />
							</linearGradient>
							<linearGradient id="maintConeWhite" x1="0" y1="0" x2="0" y2="1">
								<stop offset="0%" stopColor="#fafafa" />
								<stop offset="45%" stopColor="#ebebeb" />
								<stop offset="100%" stopColor="#cfcfcf" />
							</linearGradient>
							<pattern id="maintConeReflectGrid" width="2.2" height="2.2" patternUnits="userSpaceOnUse">
								<rect width="2.2" height="2.2" fill="transparent" />
								<circle cx="0.5" cy="0.5" r="0.32" fill="#4b5563" opacity="0.38" />
								<circle cx="1.7" cy="0.5" r="0.32" fill="#4b5563" opacity="0.38" />
								<circle cx="1.1" cy="1.28" r="0.32" fill="#4b5563" opacity="0.38" />
							</pattern>
							<clipPath id="maintConeSilhouette">
								<path d="M50 10 L20 76 H80 Z" />
							</clipPath>
						</defs>

						<ellipse cx="50" cy="100" rx="36" ry="5" fill="#0d1b2e" opacity="0.2" />

						<path d="M14 88 L18 100 H82 L86 88 V85 H14 Z" fill="#a8320f" />
						<path d="M16 86 H86 L84 88 H18 Z" fill="url(#maintConeOrange)" opacity="0.45" />
						<rect x="13" y="76.5" width="74" height="11" rx="3.8" fill="#e54a18" />
						<rect x="15.5" y="78.2" width="69" height="7" rx="2.5" fill="#ff8f42" opacity="0.88" />
						<path d="M78 76.5 L86 76.5 L87.5 78.8 L80 78.8 Z" fill="#000000" opacity="0.2" />
						<ellipse cx="50" cy="77.2" rx="24" ry="2.6" fill="#ffffff" opacity="0.2" />
						<ellipse cx="50" cy="77.8" rx="27" ry="2.8" fill="#000000" opacity="0.1" />

						<g clipPath="url(#maintConeSilhouette)">
							<path d={coneTip} fill="url(#maintConeOrange)" />
							<path d={coneWhite1} fill="url(#maintConeWhite)" />
							<path d={coneWhite1} fill="url(#maintConeReflectGrid)" opacity="0.62" />
							<path d={coneOrangeMid} fill="url(#maintConeOrange)" />
							<path d={coneWhite2} fill="url(#maintConeWhite)" />
							<path d={coneWhite2} fill="url(#maintConeReflectGrid)" opacity="0.58" />
							<path d={coneOrangeBot} fill="url(#maintConeOrange)" />
							<path d="M 49 11 L 33 75 H 38 L 50 11 Z" fill="#ffffff" opacity="0.16" />
							<path d="M 76 76 L 50 10 L 53 10 L 78 76 Z" fill="#000000" opacity="0.09" />
						</g>
						<path d="M50 10 L20 76 H80 Z" fill="none" stroke="#9e2f0c" strokeWidth="0.55" />
						<ellipse cx="50" cy="10.8" rx="5" ry="2.6" fill="#ffb366" />
						<ellipse cx="50" cy="10.2" rx="3.5" ry="1.5" fill="#ffffff" opacity="0.35" />
					</svg>
				</div>

				<div className="maintenance__right">
					<div className="maintenance__badge">
						<span className="maintenance__badge-dot" />
						{tr("Технические работы")}
					</div>

					<h1 className="maintenance__title">
						{tr("На этой странице")}
						<br />
						<span className="maintenance__title-red">{tr("ведутся технические работы")}</span>
					</h1>

					<p className="maintenance__desc">
						{tr(
							"Приносим извинения за неудобства. Страница временно недоступна — мы работаем над её восстановлением."
						)}
					</p>

					<p className="maintenance__ru">
						{tr(
							"Технические работы завершатся в ближайшее время. Спасибо за терпение."
						)}
					</p>
				</div>
			</div>
		</div>
	);
};

export default Maintenance;
